import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { Pencil, Plus, RefreshCcw, Thermometer, Trash2, X } from 'lucide-react';
import { assetsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import ConfirmModal from '../../components/common/ConfirmModal';
import Skeleton, { SkeletonText } from '../../components/common/Skeleton';
import TopProgressBar from '../../components/common/TopProgressBar';

const EMPTY_FORM = {
  id: null,
  assetName: '',
  assetType: 'fridge',
  location: '',
  temperatureMin: '',
  temperatureMax: '',
  requiresTemperatureCheck: false,
  displayOrder: 10,
  isActive: true,
};

const ASSET_TYPE_OPTIONS = [
  { value: 'fridge', label: 'Fridge' },
  { value: 'freezer', label: 'Freezer' },
  { value: 'ambient', label: 'Ambient Storage' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'other', label: 'Other' },
];

const toText = (value) => (value === null || value === undefined ? '' : String(value));

const AssetManagement = () => {
  const { hasFeatureAccess, isAdmin } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canManage = isAdmin() || hasFeatureAccess('admin.assets');
  const vendorId = selectedStore?.vendorId || selectedStore?.vendor_id || null;
  const storeInfoId = selectedStore?.storeInfoId || selectedStore?.store_info_id || null;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const storeName = selectedStore?.name || selectedStore?.storeName || 'Selected Store';

  const sortedAssets = useMemo(() => (
    [...assets].sort((left, right) => {
      const leftOrder = Number(left.displayOrder ?? left.display_order ?? 0);
      const rightOrder = Number(right.displayOrder ?? right.display_order ?? 0);
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return String(left.assetName || left.asset_name || '').localeCompare(String(right.assetName || right.asset_name || ''));
    })
  ), [assets]);

  const loadAssets = useCallback(async () => {
    if (!canManage || !vendorId) return;
    setLoading(true);
    try {
      const response = await assetsAPI.getAll({
        vendorId,
        storeInfoId,
        includeInactive: true,
      });
      setAssets(response.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [canManage, storeInfoId, vendorId]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const resetForm = () => {
    setEditing(false);
    setForm(EMPTY_FORM);
  };

  const openAddDrawer = () => {
    resetForm();
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    resetForm();
    setDrawerOpen(false);
  };

  const editAsset = (asset) => {
    setEditing(true);
    setForm({
      id: asset.id,
      assetName: asset.assetName || asset.asset_name || '',
      assetType: asset.assetType || asset.asset_type || 'other',
      location: asset.location || '',
      temperatureMin: toText(asset.temperatureMin ?? asset.temperature_min),
      temperatureMax: toText(asset.temperatureMax ?? asset.temperature_max),
      requiresTemperatureCheck: Boolean(asset.requiresTemperatureCheck ?? asset.requires_temperature_check),
      displayOrder: Number(asset.displayOrder ?? asset.display_order ?? 0),
      isActive: Boolean(asset.isActive ?? asset.is_active),
    });
    setDrawerOpen(true);
  };

  const updateForm = (field, value) => {
    setForm((prev) => {
      if (field === 'requiresTemperatureCheck' && !value) {
        return {
          ...prev,
          requiresTemperatureCheck: false,
          temperatureMin: '',
          temperatureMax: '',
        };
      }

      return { ...prev, [field]: value };
    });
  };

  const save = async (event) => {
    event.preventDefault();
    if (!vendorId) {
      toast.error('Select a store first');
      return;
    }
    if (!form.assetName.trim()) {
      toast.error('Asset name is required');
      return;
    }
    if (form.requiresTemperatureCheck) {
      if (form.temperatureMin === '' || form.temperatureMax === '') {
        toast.error('Min and max temperature are required for temperature-check assets');
        return;
      }

      const minTemperature = Number(form.temperatureMin);
      const maxTemperature = Number(form.temperatureMax);
      if (!Number.isFinite(minTemperature) || !Number.isFinite(maxTemperature)) {
        toast.error('Temperature range must be valid numbers');
        return;
      }
      if (minTemperature > maxTemperature) {
        toast.error('Min temperature cannot be greater than max temperature');
        return;
      }
    }

    setSaving(true);
    try {
      const isTemperatureAsset = Boolean(form.requiresTemperatureCheck);
      await assetsAPI.save({
        ...form,
        vendorId,
        storeInfoId,
        temperatureMin: isTemperatureAsset ? Number(form.temperatureMin) : null,
        temperatureMax: isTemperatureAsset ? Number(form.temperatureMax) : null,
      });
      toast.success(editing ? 'Asset updated' : 'Asset created');
      closeDrawer();
      await loadAssets();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (!pendingDelete?.id) return;
    setSaving(true);
    try {
      await assetsAPI.deactivate(pendingDelete.id);
      toast.success('Asset deactivated');
      setPendingDelete(null);
      await loadAssets();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to deactivate asset');
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
        You do not have access to manage assets.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TopProgressBar active={loading || saving} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Store Assets</h1>
          <p className="text-sm text-gray-600">
            Manage store assets for <span className="font-medium text-gray-900">{storeName}</span>.
            Only assets marked for temperature checks appear in the Temperature task.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={loadAssets}
            disabled={loading || !vendorId}
            className="btn-secondary inline-flex items-center justify-center gap-2"
          >
            <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={openAddDrawer}
            disabled={saving || !vendorId}
            className="btn-primary inline-flex items-center justify-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Asset
          </button>
        </div>
      </div>

      {!vendorId ? (
        <div className="rounded-md border border-gray-200 bg-white p-6 text-sm text-gray-600">
          Select a store from the header to manage its assets.
        </div>
      ) : (
        <>
          <div className="rounded-md border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">Configured Assets</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="p-4">
                    <Skeleton className="h-5 w-44" />
                    <SkeletonText className="mt-3 w-64 max-w-full" />
                  </div>
                ))
              ) : sortedAssets.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">No assets configured.</div>
              ) : (
                sortedAssets.map((asset) => {
                  const active = Boolean(asset.isActive ?? asset.is_active);
                  const tempRequired = Boolean(asset.requiresTemperatureCheck ?? asset.requires_temperature_check);
                  return (
                    <div key={asset.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium text-gray-900">{asset.assetName || asset.asset_name}</p>
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                            {asset.assetType || asset.asset_type}
                          </span>
                          {tempRequired ? (
                            <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800">Temperature</span>
                          ) : null}
                          <span className={`rounded-full px-2 py-0.5 text-xs ${active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                            {active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          {asset.location || '-'}
                          {tempRequired
                            ? ` | Range: ${asset.temperatureMin ?? asset.temperature_min ?? '-'} to ${asset.temperatureMax ?? asset.temperature_max ?? '-'} C`
                            : ''}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <button type="button" onClick={() => editAsset(asset)} className="btn-secondary inline-flex items-center gap-2">
                          <Pencil className="h-4 w-4" />
                          Edit
                        </button>
                        {active ? (
                          <button
                            type="button"
                            onClick={() => setPendingDelete(asset)}
                            className="btn-danger inline-flex items-center gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            Deactivate
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}

      {drawerOpen && createPortal((
        <div className="fixed inset-0 z-[80]">
          <div className="absolute inset-0 bg-slate-950/40" onClick={saving ? undefined : closeDrawer} />
          <div className="absolute bottom-0 right-0 top-0 flex w-full justify-end">
            <div className="relative flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl sm:max-w-xl md:max-w-2xl">
              <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {editing ? 'Edit Store Asset' : 'Add Store Asset'}
                    </h2>
                    <p className="mt-1 truncate text-sm text-gray-500">
                      {storeName}
                    </p>
                  </div>
                  <button type="button" className="btn-secondary !p-2" onClick={closeDrawer} disabled={saving}>
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
                <form id="asset-drawer-form" onSubmit={save} className="space-y-5">
                  <section className="rounded-md border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-yellow-100 text-yellow-800">
                        <Thermometer className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900">Store Asset</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Enable temperature checks only for assets that need daily temperature readings.
                        </p>
                      </div>
                    </div>
                  </section>

                  <div>
                    <label className="label" htmlFor="assetName">Asset Name</label>
                    <input
                      id="assetName"
                      className="input"
                      value={form.assetName}
                      onChange={(event) => updateForm('assetName', event.target.value)}
                      placeholder="Main Fridge"
                      disabled={saving}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="label" htmlFor="assetType">Type</label>
                      <select
                        id="assetType"
                        className="input"
                        value={form.assetType}
                        onChange={(event) => updateForm('assetType', event.target.value)}
                        disabled={saving}
                      >
                        {ASSET_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="label" htmlFor="assetDisplayOrder">Sort Order</label>
                      <input
                        id="assetDisplayOrder"
                        type="number"
                        className="input"
                        value={form.displayOrder}
                        onChange={(event) => updateForm('displayOrder', Number(event.target.value))}
                        disabled={saving}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label" htmlFor="assetLocation">Location</label>
                    <input
                      id="assetLocation"
                      className="input"
                      value={form.location}
                      onChange={(event) => updateForm('location', event.target.value)}
                      placeholder="Back Area"
                      disabled={saving}
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-3">
                      <input
                        type="checkbox"
                        checked={form.requiresTemperatureCheck}
                        onChange={(event) => updateForm('requiresTemperatureCheck', event.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        disabled={saving}
                      />
                      <span className="text-sm font-medium text-gray-700">Temperature Check</span>
                    </label>
                    <label className="flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-3">
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(event) => updateForm('isActive', event.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        disabled={saving}
                      />
                      <span className="text-sm font-medium text-gray-700">Active</span>
                    </label>
                  </div>

                  {form.requiresTemperatureCheck ? (
                    <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="label" htmlFor="assetTemperatureMin">Min Temp (C)</label>
                          <input
                            id="assetTemperatureMin"
                            type="number"
                            step="0.01"
                            className="input"
                            value={form.temperatureMin}
                            onChange={(event) => updateForm('temperatureMin', event.target.value)}
                            disabled={saving}
                            required
                          />
                        </div>

                        <div>
                          <label className="label" htmlFor="assetTemperatureMax">Max Temp (C)</label>
                          <input
                            id="assetTemperatureMax"
                            type="number"
                            step="0.01"
                            className="input"
                            value={form.temperatureMax}
                            onChange={(event) => updateForm('temperatureMax', event.target.value)}
                            disabled={saving}
                            required
                          />
                        </div>
                      </div>
                      <p className="mt-3 text-xs text-yellow-800">
                        Temperature range is required because this asset will appear in the daily Temperature task.
                      </p>
                    </div>
                  ) : null}
                </form>
              </div>

              <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-4 sm:px-6">
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button type="button" className="btn-secondary" onClick={closeDrawer} disabled={saving}>
                    Cancel
                  </button>
                  <button type="submit" form="asset-drawer-form" className="btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Asset'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ), document.body)}

      <ConfirmModal
        isOpen={!!pendingDelete}
        title="Deactivate Asset"
        message={`Deactivate ${pendingDelete?.assetName || pendingDelete?.asset_name || 'this asset'}? Existing temperature logs will remain available.`}
        confirmLabel="Deactivate"
        variant="danger"
        loading={saving}
        onClose={() => setPendingDelete(null)}
        onConfirm={deactivate}
      />
    </div>
  );
};

export default AssetManagement;
